import { RpcOptions, ServiceInfo, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { useOAuth } from 'lib/rpc'
import { useClientRPC } from 'lib/rpcts'
import { AllowedRefs, convertToRefString, WebsocketNotifyClient } from 'lib/websocket-notify'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { multiGetSubscriptionManager } from './GRPCSubscriptionMultiGet'
import { queued, requested, fulfilled, rejected, markStale } from './multigetActions'
import { makeSelectObjectByRef } from './multigetSelectors'
import { SerializedErrorType, serializedError } from './reducer'
import { clientTypeName, GRPCClient } from './utils'

export type MultiGetHookResponse<ResponseType> = {
    readonly isLoading: boolean
    readonly isError: boolean
    readonly isStale: boolean
    readonly data?: Readonly<ResponseType>
    readonly error?: Readonly<SerializedErrorType>
}

export type MultiGetHook<ResponseType = unknown, Ref extends AllowedRefs = AllowedRefs> = (
    ref: Ref,
    flags?: MultiGetHookFlags
) => MultiGetHookResponse<ResponseType>

export type MultiGetLazyHook<
    ResponseType = unknown,
    Ref extends AllowedRefs = AllowedRefs
> = () => {
    trigger: (ref: Ref, flags?: MultiGetHookFlags) => Promise<ResponseType>
} & MultiGetHookResponse<ResponseType>

export type MultiGetInvalidateHook<Ref extends AllowedRefs = AllowedRefs> = (
    ref?: Ref
) => () => void

export type MultiGetHookFlags = {
    force?: boolean
    actionable?: boolean
}

const DEFAULT_HOOK_FLAGS: Required<MultiGetHookFlags> = {
    force: false,
    actionable: true,
}

export interface MultiGetOpts<Ref, Req, Res, Item> {
    // Build the RPC request from a list of **typed** refs. The library will
    // pass the *same* Ref objects that callers hand to the hook.
    buildRequest: (refs: Ref[]) => Req

    // Extract the returned objects array from the RPC response. Defaults to
    // `(r: unknown) => r.list || []` if omitted.
    transformResponse?: (res: Res) => Item[]

    // Convert a returned Item into the *canonical* Ref object so we can map
    // it back into the cache.
    refExtractor?: (item: Item) => Ref

    // Configuration options
    windowMs?: number
    maxBatchSize?: number
    enableWebsocketRefresh?: boolean

    // Set to true if you want to be notified when refs that don't exist yet are created
    watchInserts?: boolean

    // MUST be set to the typeName of the ref object.
    tag: string
}

const DEFAULT_OPTS = {
    windowMs: 10,
    maxBatchSize: 100,
    enableWebsocketRefresh: true,
    watchInserts: false,
}

// Internal retry configuration
const MAX_RETRIES = 3
const MAX_BACKOFF_MS = 15000

/**
 * Creates a set of hooks and a reducer entry to manage batched object data
 * for a specific gRPC service method. The generated hooks provide batching,
 * caching, and invalidation capabilities.
 *
 * @param {GRPCClient<Client>} ClientConstructor The protobuf-generated constructor for your generated gRPC client.
 * @param {Method} methodName The gRPC method name that returns a batched response.
 * @param {MultiGetOpts<Ref, Req, Res, Item>} opts Configuration options for the multi-get API.
 * @returns {Object} An object containing hooks for interacting with the batched data.
 */
export const createMultiGetApi = <
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Req extends object,
    Res extends object = object,
    Ref extends AllowedRefs = AllowedRefs,
    Item = unknown,
>(
        ClientConstructor: GRPCClient<Client>,
        methodName: Method,
        opts: MultiGetOpts<Ref, Req, Res, Item>,
    ): {
    useMultiGet: MultiGetHook<Item, Ref>
    useMultiGetInvalidate: MultiGetInvalidateHook<Ref>
} => {
    const config = { ...DEFAULT_OPTS, ...opts }

    // Cache key format: {tag}:{serviceName}.{methodName}
    // The tag prefix allows the reducer to find all caches for a given tag during invalidation.
    // This prevents collisions when multiple endpoints return different data types
    // but are keyed by the same ref type (e.g., AppRef).
    const serviceName = clientTypeName(ClientConstructor) || ClientConstructor.name
    if (!serviceName) {
        throw new Error(`createMultiGetApi: Unable to determine service name for method ${methodName}`)
    }
    const cacheKey = `${config.tag}:${serviceName}.${methodName}`

    // Local batching state – one per createMultiGetApi invocation
    // Use Map<refString, Ref> to deduplicate by value, not object reference
    // This ensures that multiple components requesting the same ref don't cause duplicate fetches
    let pendingRefs = new Map<string, Ref>()
    let batchTimer: NodeJS.Timeout | undefined
    let inFlight = false

    // Unique key for this subscription instance: methodName + cryptoUUID
    const generateSubKey = (): string => `${ClientConstructor.name}.${methodName}_${crypto.randomUUID()}`

    // Flush batch function
    const flushBatch = async (
        dispatch: ThunkDispatch<unknown, never, UnknownAction>,
        client: Client,
        retryCount = 0,
    ): Promise<void> => {
        if (inFlight || !pendingRefs || pendingRefs.size === 0) return

        inFlight = true
        const refsToFetch = Array.from(pendingRefs.values())
        pendingRefs.clear()

        if (refsToFetch.length === 0) {
            inFlight = false
            return
        }

        // Take only the first maxBatchSize entries
        const batchRefs = refsToFetch.slice(0, config.maxBatchSize)
        const remainingRefs = refsToFetch.slice(config.maxBatchSize)

        // Put any remaining refs back in the pending queue
        if (remainingRefs.length > 0) {
            for (const ref of remainingRefs) {
                const refString = convertToRefString(ref, config.tag)
                if (refString) {
                    pendingRefs.set(refString, ref)
                }
            }
        }

        // Dispatch requested action for this batch
        dispatch(requested({ type: cacheKey, refs: batchRefs }))

        try {
            const request = config.buildRequest(batchRefs)

            const method = client[methodName] as (input: Req, options?: RpcOptions) => UnaryCall<Req, Res>
            const call = method.call(client, request)
            const rpcResponse = await call.response

            const items = config.transformResponse
                ? config.transformResponse(rpcResponse)
                : (rpcResponse as { list?: Item[] }).list || []

            // Build objects map
            // Note: convertToRefString uses config.tag (proto type name) for proper ref extraction
            const objects: Record<string, Item> = {}
            const errors: Record<string, SerializedErrorType> = {}

            for (const item of items) {
                const ref = config.refExtractor ? config.refExtractor(item) : (item as { ref?: Ref }).ref
                if (ref) {
                    const key = convertToRefString(ref, config.tag)
                    if (key) objects[key] = item
                }
            }

            // Handle missing refs as errors
            for (const ref of batchRefs) {
                const refString = convertToRefString(ref, config.tag)
                if (refString && !objects[refString]) {
                    errors[refString] = {
                        message: 'Object not found',
                        name: 'NotFoundError',
                        code: 'NOT_FOUND',
                    }
                }
            }

            // Dispatch results
            if (Object.keys(objects).length > 0) {
                dispatch(fulfilled({ type: cacheKey, objects }))
            }
            if (Object.keys(errors).length > 0) {
                dispatch(rejected({ type: cacheKey, errors }))
            }
        } catch (error) {
            // Handle RPC errors with retry logic
            if (retryCount < MAX_RETRIES) {
                // Exponential backoff: 1s, 2s, 4s, ... capped at 15s
                const delayMs = Math.min(1000 * 2 ** retryCount, MAX_BACKOFF_MS)

                // Re-add refs to pending queue for retry
                for (const ref of batchRefs) {
                    const refString = convertToRefString(ref, config.tag)
                    if (refString) {
                        pendingRefs.set(refString, ref)
                    }
                }

                inFlight = false

                // Schedule retry with backoff
                setTimeout(() => {
                    void flushBatch(dispatch, client, retryCount + 1)
                }, delayMs)
                return
            }

            // Max retries exceeded - dispatch terminal error
            const errors: Record<string, SerializedErrorType> = {}
            for (const ref of batchRefs) {
                const refString = convertToRefString(ref, config.tag)
                if (refString) {
                    errors[refString] = serializedError(error)
                }
            }
            dispatch(rejected({ type: cacheKey, errors }))
        }

        inFlight = false

        // Process any remaining refs (including those that were put back)
        // Reset retryCount to 0 for new batches (these are fresh refs, not retries)
        if (pendingRefs && pendingRefs.size > 0) {
            setTimeout(() => flushBatch(dispatch, client, 0), 0)
        }
    }

    // Queue refs for batching
    const queueRefs = (
        dispatch: ThunkDispatch<unknown, never, UnknownAction>,
        client: Client,
        refs: Ref[],
    ): void => {
        if (!pendingRefs) {
            pendingRefs = new Map()
        }

        for (const ref of refs) {
            const refString = convertToRefString(ref, config.tag)
            if (refString) {
                pendingRefs.set(refString, ref)
            }
        }

        dispatch(queued({ type: cacheKey, refs }))

        // Start batch timer if not already running
        if (!batchTimer && !inFlight) {
            batchTimer = setTimeout(() => {
                batchTimer = undefined
                void flushBatch(dispatch, client)
            }, config.windowMs)
        }
    }

    // Hook implementation
    const useMultiGetHook: MultiGetHook<Item, Ref> = (ref, _flags?: MultiGetHookFlags) => {
        const flags = { ...DEFAULT_HOOK_FLAGS, ..._flags }
        const dispatch: ThunkDispatch<unknown, never, UnknownAction> = useDispatch()
        const client = useClientRPC(ClientConstructor)
        const authClient = useOAuth()
        const notifyClient = useMemo(() => (
            config.enableWebsocketRefresh && config.tag
                ? WebsocketNotifyClient.getInstance(authClient)
                : undefined
        ), [authClient])

        const subKeyRef = useRef<string | undefined>(undefined)

        // Generate stable subscription key for this hook instance
        if (!subKeyRef.current) {
            subKeyRef.current = generateSubKey()
        }

        // Subscribe to websocket events via WebsocketFilterManager
        useEffect(() => {
            if (!flags.actionable) return
            if (!notifyClient || !config.enableWebsocketRefresh) return

            const subKey = subKeyRef.current
            if (!subKey) return

            // Subscribe using config.tag (proto type name) for WebSocket matching with backend,
            // but dispatch markStale using cacheKey for Redux state updates
            multiGetSubscriptionManager.registerMultiGetSubscription(subKey, config.tag, config.watchInserts ?? true)

            notifyClient.subscribe([config.tag], subKey, (changedRef: AllowedRefs): void => {
                dispatch(markStale({ type: cacheKey, ref: changedRef }))
            })

            return () => {
                notifyClient.unsubscribe([config.tag], subKey)
                multiGetSubscriptionManager.unregisterMultiGetSubscription(subKey)
            }
        }, [notifyClient, dispatch, flags.actionable])

        // Update WebsocketFilterManager with current ref
        useEffect(() => {
            if (!flags.actionable) return
            if (!config.enableWebsocketRefresh || !ref) return

            const subKey = subKeyRef.current
            if (!subKey) return

            const refString = convertToRefString(ref, config.tag)

            if (refString) {
                multiGetSubscriptionManager.updateMultiGetRefs(subKey, [refString])
            }

            return () => {
                if (subKey) multiGetSubscriptionManager.updateMultiGetRefs(subKey, [])
            }
        }, [ref, flags.actionable])

        // Use selector for cache lookup (tag is derived from cacheKey format)
        const selector = useMemo(() => makeSelectObjectByRef(cacheKey, ref), [ref])
        const cache = useSelector(selector) as MultiGetHookResponse<Item>

        // Queue ref for fetching
        useEffect(
            () => {
                if (!client || !ref || !flags.actionable) return

                // All errors are terminal here because:
                // - NOT_FOUND: object genuinely doesn't exist, no point retrying
                // - Other errors: already exhausted MAX_RETRIES (3) with exponential backoff in flushBatch
                // Recovery paths: websocket markStale, force: true flag, or component remount
                // Also skip if already loading to prevent duplicate fetches from multiple components
                const shouldFetch = ((!cache.data && !cache.isError && !cache.isLoading) || cache.isStale || flags.force)
                if (shouldFetch) {
                    queueRefs(dispatch, client, [ref])
                }
            },
            [client, ref, cache.data, cache.isError, cache.isLoading, cache.isStale, flags.force, flags.actionable, dispatch],
        )

        return {
            isLoading: !!cache.isLoading,
            isError: !!cache.isError,
            isStale: !!cache.isStale,
            data: cache.data,
            error: cache.error,
        }
    }

    // Invalidate hook implementation
    const useMultiGetInvalidateHook: MultiGetInvalidateHook<Ref> = (ref) => {
        const dispatch: ThunkDispatch<unknown, never, UnknownAction> = useDispatch()

        return useCallback(() => {
            if (ref) {
                dispatch(markStale({ type: cacheKey, ref }))
            }
        }, [dispatch, ref])
    }

    return {
        useMultiGet: useMultiGetHook,
        useMultiGetInvalidate: useMultiGetInvalidateHook,
    }
}
